// 도감 2.0 레지스트리: 85칸(몬스터 24 + 보스 13 + 도안 8 + 펫 6 + 유니크 6 + 아바타 28)을 데이터 원천에서 만든다.
// 정본 docs/prd/c6-codex-alliance.md 4.1, 계약 docs/prd/c6-codex-1-contract.md 1.1. 순수(three·DOM import 금지).
// 원천의 개수가 PRD와 다르면 칸 수도 따라 달라지고, codex.test.ts가 85칸·범주별 개수를 단언해 실패한다(조용히 맞추지 않는다).
import { MobKind } from '../types';
import { BOSS_DEFS, BOSS_KEYS } from '../content/bosses';
import { PETS, RAIDS, SETS, NAMED_UNIQUES } from '../content/items';
import { REGIONS_90D } from '../content/world';
import { AVATARS } from '../avatar';

export type CodexCat = 'monster' | 'boss' | 'set' | 'pet' | 'unique' | 'avatar';
export interface CodexCell {
  /** `m:<지역>:<MobKind>` · `b:<BossKey>` · `b:raid_ember` · `s:<세트 id>` · `p:<펫 id>` · `u:<유니크 id>` · `a:<아바타 id>` */
  readonly id: string;
  readonly cat: CodexCat;
  readonly name: string;
  /** 1..6, 지역이 없으면 0(레이드 세트·지역 없는 아바타) */
  readonly region: number;
}

/** 도감 범주 순서(탭 순서) */
export const CODEX_CATS: readonly CodexCat[] = ['monster', 'boss', 'set', 'pet', 'unique', 'avatar'];
export const CODEX_CAT_NAME: Readonly<Record<CodexCat, string>> = { monster: '몬스터', boss: '보스', set: '도안', pet: '펫', unique: '유니크', avatar: '아바타' };
/** 잿불 비룡 레이드 보스 칸 id(보스 정산에서 b:raid_ember만 +1) */
export const CODEX_RAID_BOSS_ID = 'b:raid_ember';

/** 몬스터 칸 id */
export function monsterCellId(region: number, kind: number): string { return 'm:' + region + ':' + kind; }
/** 보스 칸 id (BossKey) */
export function bossCellId(key: string): string { return 'b:' + key; }

const RAID_BOSS_NAME = RAIDS.find((r) => r.id === 'raid_wyvern')?.name ?? '잿불 비룡';

function build(): CodexCell[] {
  const out: CodexCell[] = [];
  const kinds = Object.values(MobKind) as number[];
  // 몬스터: 지역(REGIONS_90D) × MobKind. 이름은 지역 몹 표시 이름, 없으면 "지역명 + 종류 번호"
  for (let r = 0; r < REGIONS_90D.length; r++) {
    const reg = REGIONS_90D[r];
    for (const k of kinds) out.push({ id: monsterCellId(r + 1, k), cat: 'monster', name: reg.mobs[k] ?? reg.name + ' 몬스터 ' + (k + 1), region: r + 1 });
  }
  for (const key of BOSS_KEYS) { const d = BOSS_DEFS[key]; out.push({ id: bossCellId(key), cat: 'boss', name: d.name, region: d.region }); }
  out.push({ id: CODEX_RAID_BOSS_ID, cat: 'boss', name: RAID_BOSS_NAME, region: 2 });
  for (const s of SETS) out.push({ id: 's:' + s.id, cat: 'set', name: s.name, region: typeof s.region === 'number' ? s.region : 0 });
  for (const p of PETS) out.push({ id: 'p:' + p.id, cat: 'pet', name: p.name, region: p.region });
  for (const u of NAMED_UNIQUES) out.push({ id: 'u:' + u.id, cat: 'unique', name: u.name, region: u.source.region });
  for (const a of AVATARS) out.push({ id: 'a:' + a.id, cat: 'avatar', name: a.name, region: a.source.region ?? 0 });
  return out;
}

/** 85칸(범주 순서 → 원천 순서). 셀 인덱스는 이 배열 위치이며 이벤트·뷰가 쓴다 */
export const CODEX_CELLS: readonly CodexCell[] = build();
export const CODEX_CAT_SIZE: Readonly<Record<CodexCat, number>> = (() => {
  const o: Record<CodexCat, number> = { monster: 0, boss: 0, set: 0, pet: 0, unique: 0, avatar: 0 };
  for (const c of CODEX_CELLS) o[c.cat]++;
  return o;
})();
const INDEX = new Map<string, number>(CODEX_CELLS.map((c, i) => [c.id, i]));
/** 칸 id → 인덱스(없으면 -1) */
export function codexCellIndex(id: string): number { return INDEX.get(id) ?? -1; }
/** 처치 수로 저장하는 칸(몬스터·보스)의 id 집합 — 저장·서버 검사가 쓴다 */
export const CODEX_KILL_IDS: ReadonlySet<string> = new Set(CODEX_CELLS.filter((c) => c.cat === 'monster' || c.cat === 'boss').map((c) => c.id));
